/**
 * diffSeverity — classifies a Review-Changes dialog item as either
 * 'attention' (a regression or status flip the user should review
 * before applying) or 'normal' (default-Apply). Pure helper so the
 * dialog can sort + default per-card selection deterministically.
 *
 * The two attention triggers:
 *   1. Quality regression: the Quality / Fact-Check / Evidence /
 *      OverallScore drops by ≥ 20 percentage-points.
 *   2. Status flip across the GREEN / AMBER / RED axis (overall
 *      status, verdict label).
 *
 * Both surface as "Needs attention" in the dialog header and block
 * the Apply button until the user picks Keep or Apply on each
 * flagged card. Everything else defaults to 'normal'.
 */

export type DiffSeverity = 'attention' | 'normal';

export interface SeverityDecision {
  severity: DiffSeverity;
  /** Short label rendered as a chip on the card header. */
  badge?: string;
  /** Net delta in score points (positive = improvement, negative =
   *  regression). Undefined for non-numeric items. */
  delta?: number;
}

const REGRESSION_THRESHOLD_PT = 20;
const NUMERIC_KEYS = new Set([
  'overallScore',
  'qualityReview',
  'factCheck',
  'sedCompleteness',
]);
const STATUS_AXIS_KEYS = new Set([
  'overallStatus',
  'conclusion',
]);

/** Parse a "X/100" or "X%" or "X" header into a number. Returns
 *  NaN when unparseable; the caller treats that as 'normal'. */
function parseScore(value: string): number {
  const m = value.match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]!) : NaN;
}

export function classifyDiff(section: string, oldValue: string, newValue: string): SeverityDecision {
  // Score-style regressions: parse both sides and check delta.
  if (NUMERIC_KEYS.has(section)) {
    const oldN = parseScore(oldValue);
    const newN = parseScore(newValue);
    if (Number.isFinite(oldN) && Number.isFinite(newN)) {
      const delta = newN - oldN;
      if (delta <= -REGRESSION_THRESHOLD_PT) {
        return {
          severity: 'attention',
          badge: `regression −${Math.abs(delta).toFixed(0)}pt`,
          delta,
        };
      }
      return { severity: 'normal', delta };
    }
  }

  // Status flips across the RAG axis.
  if (STATUS_AXIS_KEYS.has(section)) {
    const oldNorm = normaliseStatus(oldValue);
    const newNorm = normaliseStatus(newValue);
    if (oldNorm && newNorm && oldNorm !== newNorm) {
      return {
        severity: 'attention',
        badge: `verdict flip ${oldNorm} → ${newNorm}`,
      };
    }
  }

  return { severity: 'normal' };
}

function normaliseStatus(s: string): 'green' | 'amber' | 'red' | null {
  const t = s.trim().toLowerCase();
  if (!t) return null;
  if (t === 'green' || /^proceed\b(?!\s+with)/.test(t)) return 'green';
  if (t === 'amber' || /^proceed\s+with/.test(t)) return 'amber';
  if (t === 'red' || /^do\s+not/.test(t)) return 'red';
  return null;
}
